// Type-specific Report Builder templates (Reports > Project Reports: summary / field / qc / anomaly).
// Pure helpers: ReportBuilder fetches the project's datasets and passes them in.
import { qcDigest } from '../components/QcCard';

export const REPORT_TYPES = ['summary', 'field', 'qc', 'anomaly'];
export const REPORT_TITLES = {
  summary: 'Project Summary',
  field: 'Field Report',
  qc: 'QC Report',
  anomaly: 'Anomaly Report',
};

const fmtPct = (v) => (typeof v === 'number' && Number.isFinite(v) ? `${v.toFixed(1)}%` : 'n/a');

/** One row per dataset for the QC table, from each dataset's stored `metadata.qc`. */
export function qcTableRows(datasets) {
  return (datasets || []).map((ds) => {
    const d = qcDigest(ds?.metadata?.qc);
    const name = ds?.name || '(unnamed)';
    if (!d) return { Dataset: name, A: '-', B: '-', C: '-', D: '-', '% D': 'n/a', Flags: 'QC not run' };
    const flags = [`${d.nFlagged} C/D`];
    if (d.lowSignal) flags.push(`${d.lowSignal} low signal`);
    if (d.remote) flags.push('remote');
    const counts = Object.fromEntries(d.counts);
    return { Dataset: name, A: counts.A, B: counts.B, C: counts.C, D: counts.D, '% D': fmtPct(d.pctD), Flags: flags.join(', ') };
  });
}

/** "Dataset: warning" lines from every dataset's stored QC summary. */
export function qcWarnings(datasets) {
  const out = [];
  (datasets || []).forEach((ds) => {
    const d = qcDigest(ds?.metadata?.qc);
    (d?.warnings || []).forEach((w) => out.push(`${ds.name || 'Dataset'}: ${typeof w === 'string' ? w : JSON.stringify(w)}`));
  });
  return out;
}

export function datasetTableRows(datasets) {
  return (datasets || []).map((ds) => ({
    Dataset: ds?.name || '(unnamed)',
    Method: ds?.method_type || '',
    QC: ds?.qc_status || ds?.status || '',
    Created: ds?.created_at ? String(ds.created_at).slice(0, 10) : '',
  }));
}

/** Rows for the anomaly report: result/anomaly layers of the open map. */
export function resultLayerRows(resultLayers) {
  return (resultLayers || []).map((l) => ({
    Layer: l.name || l.id,
    Type: l.kind || 'result',
    Features: l.geojson?.features?.length ?? '',
    Visible: l.visible === false ? 'no' : 'yes',
  }));
}

const today = () => new Date().toISOString().slice(0, 10);
const DS_COLS = ['Dataset', 'Method', 'QC', 'Created'];

/**
 * Elements (builder coordinates, px) for a report type.
 * ctx: { projectName, project, datasets, resultLayers, mapSnapshot, pageW, pageH, nextId }
 */
export function buildReportElements(type, ctx) {
  const { projectName = 'Project', project = null, datasets = [], resultLayers = [], mapSnapshot = null, pageW, pageH, nextId } = ctx;
  const t = REPORT_TYPES.includes(type) ? type : 'summary';
  const m = 24;
  const innerW = pageW - m * 2;
  const els = [];
  let y = m;
  const push = (el) => { els.push({ id: nextId(), ...el }); };
  const add = (el, h) => { push({ x: m, y, h, ...el }); y += h + 12; };
  const rowsH = (n) => Math.min(Math.max(60, 22 + n * 14), Math.max(80, pageH - y - m));
  const table = (columns, rows, max) => add({ type: 'table', w: innerW, columns, rows }, rowsH(Math.min(rows.length || 1, max)));

  add({ type: 'title', w: innerW, text: `${projectName} - ${REPORT_TITLES[t]}`, fontSize: 24, bold: true }, 44);

  const mapH = Math.round(pageH * 0.4);
  if (t === 'summary') {
    const info = [
      `Project: ${projectName}`,
      project?.description ? `Description: ${project.description}` : null,
      project?.location ? `Location: ${project.location}` : null,
      `Datasets: ${datasets.length}`,
      `Date: ${today()}`,
    ].filter(Boolean).join('\n');
    add({ type: 'text', w: innerW, text: info, fontSize: 11 }, 70);
    const mw = Math.round(innerW * 0.62);
    push({ type: 'map', x: m, y, w: mw, h: mapH, src: mapSnapshot });
    push({ type: 'legend', x: m + mw + 12, y, w: innerW - mw - 12, h: 120 });
    y += mapH + 12;
    table(DS_COLS, datasetTableRows(datasets), 10);
  } else if (t === 'field') {
    add({ type: 'map', w: innerW, src: mapSnapshot }, mapH);
    const hw = Math.round(innerW / 2) - 6;
    push({ type: 'text', x: m, y, w: hw, h: 70, fontSize: 11, text: 'Date: ____ / ____ / ________\nCrew: ______________________\nWeather: ____________________' });
    push({ type: 'text', x: m + hw + 12, y, w: hw, h: 70, fontSize: 11, text: 'Site conditions: ____________\nEquipment: _________________\nArray / spacing: ____________' });
    y += 82;
    add({ type: 'text', w: innerW, text: 'Field notes:\n\n', fontSize: 11 }, 90);
    table(DS_COLS, datasetTableRows(datasets), 10);
  } else if (t === 'qc') {
    table(['Dataset', 'A', 'B', 'C', 'D', '% D', 'Flags'], qcTableRows(datasets), 20);
    const warns = qcWarnings(datasets);
    add({ type: 'text', w: innerW, text: warns.length ? `Warnings:\n${warns.join('\n')}` : 'Warnings: none reported.', fontSize: 11 }, Math.max(50, 24 + warns.length * 15));
  } else {
    add({ type: 'map', w: innerW, src: mapSnapshot }, mapH);
    table(['Layer', 'Type', 'Features', 'Visible'], resultLayerRows(resultLayers), 12);
  }
  return els;
}
